import type { Point, PointArray } from '../types/contour-definition-array-type';

import { insetPolygon } from './insetPolygon';
import { isPointWithinPolygon } from './isPointWithinPolygon';

/**
 * Clamps an open LAX endo polyline so it stays inside the open LAX epi with a
 * small gap.
 *
 * The epi (an open curve) is closed exactly like a SAX contour: its two open
 * ends are joined by a straight base line (the valve plane), turning it into a
 * simple closed polygon. That polygon is eroded by `gap`, then every endo point
 * outside it is projected back onto it — so the endo can no longer bulge past
 * the epi wall anywhere, and its open ends terminate at (just inside) the base.
 *
 * Performance: this uses the full-resolution epi (no decimation) so the
 * region matches the true epi wall. Because the endo is a dense sub-pixel
 * polyline, no endo segment can cross the epi wall once its endpoints are kept
 * inside the eroded region. It stays O(n·m) with cheap planar tests (no exact
 * boolean geometry).
 *
 * Returns the (possibly) adjusted endo. When nothing needs to change the input
 * reference is returned unchanged so callers can skip a reprocess. Returns
 * `null` when the inputs are degenerate.
 */
export function clampOpenEndoWithinEpi(
  endo: PointArray,
  epi: PointArray,
  gap = 0.5,
): PointArray | null {
  if (endo.length < 2 || epi.length < 2) return null;

  const { region } = buildOpenContourRegion(epi);

  let erodedRegion = region;
  try {
    const inset = insetPolygon(region, gap);
    if (inset.length >= 4) erodedRegion = inset;
  }
  catch {
    erodedRegion = region;
  }

  let changed = false;
  const adjusted: PointArray = Array.from({ length: endo.length });
  for (let i = 0; i < endo.length; i++) {
    const p = endo[i];
    if (isPointWithinPolygon(p, erodedRegion)) {
      adjusted[i] = p;
      continue;
    }
    adjusted[i] = nearestPointOnPolyline(p, erodedRegion);
    changed = true;
  }

  return changed ? adjusted : endo;
}

/**
 * Detects whether an open epi is (largely) drawn inside the endo — i.e. the
 * hierarchy is inverted and the drawing should be discarded.
 *
 * The endo is capped into a closed region; the fraction of apical-side epi
 * points that fall inside that region is measured. In a valid drawing the epi
 * surrounds the endo, so its apical points sit outside the endo region; when
 * the epi is drawn inside the endo, most of them fall inside.
 *
 * @returns `true` when the epi is (largely) inside the endo region.
 */
export function isOpenEpiInsideEndo(
  epi: PointArray,
  endo: PointArray,
  threshold: number = 0.6,
): boolean {
  if (epi.length < 2 || endo.length < 2) return false;

  const { region, baseMid, outward } = buildOpenContourRegion(endo);

  let inside = 0;
  let total = 0;
  for (const p of epi) {
    // only consider the apical side; open-end points are always allowed out
    const side = (p.x - baseMid.x) * outward.x + (p.y - baseMid.y) * outward.y;
    if (side > 0) continue;
    total++;
    if (isPointWithinPolygon(p, region)) inside++;
  }

  if (total === 0) return false;
  return inside / total >= threshold;
}

/**
 * Turns an open contour into a closed region by joining its two open ends with
 * a straight base line (the valve plane), exactly like a SAX contour's closing
 * edge. Returns the region plus the base midpoint and the outward (apex -> base)
 * unit direction (used to tell the open-end side from the apical side).
 */
function buildOpenContourRegion(open: PointArray): {
  region: PointArray;
  baseMid: Point;
  outward: Point;
} {
  const a = open[0];
  const b = open[open.length - 1];
  const baseMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

  // apex ~ vertex farthest from the base midpoint
  let apex = open[0];
  let apexDist = -1;
  for (const p of open) {
    const d = (p.x - baseMid.x) ** 2 + (p.y - baseMid.y) ** 2;
    if (d > apexDist) {
      apexDist = d;
      apex = p;
    }
  }

  // outward = from apex toward the open end (base)
  let ox = baseMid.x - apex.x;
  let oy = baseMid.y - apex.y;
  let olen = Math.hypot(ox, oy);
  if (olen < 1e-6) {
    // fall back to the base-line normal
    ox = -(b.y - a.y);
    oy = b.x - a.x;
    olen = Math.hypot(ox, oy) || 1;
  }
  ox /= olen;
  oy /= olen;

  // closed region: open curve (a..b) closed straight back to a (the base line)
  const region: PointArray = [...open, { x: a.x, y: a.y }];

  return { region, baseMid, outward: { x: ox, y: oy } };
}

function nearestPointOnPolyline(point: Point, polygon: PointArray): Point {
  let best: Point = polygon[0];
  let bestDist = Infinity;
  for (let i = 0; i < polygon.length - 1; i++) {
    const c = closestOnSegment(point, polygon[i], polygon[i + 1]);
    const d = (c.x - point.x) ** 2 + (c.y - point.y) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

function closestOnSegment(p: Point, a: Point, b: Point): Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-9) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * dx, y: a.y + t * dy };
}
